import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, FileText, Check } from 'lucide-react';
import { pickLang, RTL_LANGS } from '@/utils/localizedStrings';

/**
 * PrivacyConsentModal
 *
 * Shown ONCE on the very first launch (no consent stored yet) to comply with:
 *   • Huawei AppGallery Review Guidelines rule 7.5 (privacy notice on first run)
 *   • GDPR / general privacy best practice
 *
 * After the user taps "Agree" we persist the consent in localStorage so the
 * modal never appears again on this device.
 *
 * 13 languages supported — text is intentionally short and scannable.
 */

const STORAGE_KEY = 'falak.privacy_consent.v1';

const L = {
  en: {
    welcome: 'Welcome to Falak',
    intro: 'Before you start, please review how Falak handles your data.',
    points: [
      'Your location is used to calculate prayer times and astronomical data — it is sent to the Falak server only for the calculation and is never stored.',
      'No personal data is collected, no ads, no tracking, no third-party analytics.',
      'Only the feedback you choose to send (and your optional email) is saved to our database — plus, if you turn on prayer alerts, your device\'s notification token and upcoming prayer times.',
    ],
    fullPolicy: 'Read the full Privacy Policy',
    agree: 'I Agree & Continue',
    footer: 'By tapping "I Agree" you confirm you have read and accepted our Privacy Policy.',
  },
  ar: {
    welcome: 'مرحباً بك في فَلَك',
    intro: 'قبل أن تبدأ، يرجى الاطلاع على كيفية تعامل فلك مع بياناتك.',
    points: [
      'موقعك يُستخدم لحساب مواقيت الصلاة والبيانات الفلكية — ويُرسل إلى خادم فلك لإجراء الحساب فقط، ولا يُحفظ.',
      'لا نجمع أي بيانات شخصية، ولا إعلانات، ولا تتبّع، ولا تحليلات من أطراف ثالثة.',
      'فقط الملاحظات التي تختار إرسالها (وبريدك الإلكتروني اختياري) تُحفظ في قاعدة بياناتنا — وإذا فعّلت إشعارات الصلاة: رمز الإشعار الخاص بجهازك ومواقيت صلاتك القادمة.',
    ],
    fullPolicy: 'اقرأ سياسة الخصوصية كاملة',
    agree: 'موافق وأكمل',
    footer: 'بالضغط على "موافق" فإنك تؤكد قراءتك وموافقتك على سياسة الخصوصية.',
  },
  id: {
    welcome: 'Selamat datang di Falak',
    intro: 'Sebelum memulai, mohon tinjau bagaimana Falak menangani data Anda.',
    points: [
      'Lokasi Anda digunakan untuk menghitung waktu salat dan data astronomi — dikirim ke server Falak hanya untuk perhitungan dan tidak pernah disimpan.',
      'Tidak ada data pribadi yang dikumpulkan, tidak ada iklan, tidak ada pelacakan, tidak ada analitik pihak ketiga.',
      'Hanya masukan yang Anda kirim (dan email opsional) yang disimpan di database kami — serta, jika Anda mengaktifkan pengingat salat, token notifikasi perangkat dan waktu salat mendatang.',
    ],
    fullPolicy: 'Baca Kebijakan Privasi selengkapnya',
    agree: 'Saya Setuju & Lanjut',
    footer: 'Dengan menekan "Saya Setuju" Anda mengonfirmasi telah membaca dan menerima Kebijakan Privasi kami.',
  },
  ur: {
    welcome: 'فلک میں خوش آمدید',
    intro: 'شروع کرنے سے پہلے، براہ کرم دیکھیں کہ فلک آپ کے ڈیٹا کو کیسے سنبھالتا ہے۔',
    points: [
      'آپ کا مقام نماز کے اوقات اور فلکی ڈیٹا کے حساب کے لیے استعمال ہوتا ہے — صرف حساب کے لیے فلک کے سرور پر بھیجا جاتا ہے اور محفوظ نہیں کیا جاتا۔',
      'کوئی ذاتی ڈیٹا جمع نہیں کیا جاتا، کوئی اشتہار نہیں، کوئی ٹریکنگ نہیں، کوئی فریق ثالث تجزیہ نہیں۔',
      'صرف وہ تاثرات جو آپ بھیجنے کا انتخاب کرتے ہیں (اور آپ کا اختیاری ای میل) ہمارے ڈیٹا بیس میں محفوظ ہیں — اور اگر آپ نماز کی اطلاعات آن کریں تو آپ کے آلے کا اطلاعاتی کوڈ اور آنے والے نماز کے اوقات۔',
    ],
    fullPolicy: 'مکمل پرائیویسی پالیسی پڑھیں',
    agree: 'میں متفق ہوں اور جاری رکھیں',
    footer: '"میں متفق ہوں" دبا کر آپ تصدیق کرتے ہیں کہ آپ نے ہماری پرائیویسی پالیسی پڑھ اور قبول کی ہے۔',
  },
  fa: {
    welcome: 'به فلک خوش آمدید',
    intro: 'پیش از شروع، لطفاً نحوه‌ی مدیریت داده‌های شما توسط فلک را مرور کنید.',
    points: [
      'موقعیت شما برای محاسبه‌ی اوقات نماز و داده‌های نجومی استفاده می‌شود — فقط برای محاسبه به سرور فلک ارسال می‌شود و ذخیره نمی‌گردد.',
      'هیچ داده‌ی شخصی جمع‌آوری نمی‌کنیم، تبلیغی نداریم، ردیابی نمی‌کنیم و از ابزارهای تحلیلی شخص ثالث استفاده نمی‌کنیم.',
      'فقط بازخوردی که خودتان ارسال می‌کنید (و ایمیل اختیاری شما) در پایگاه داده‌ی ما ذخیره می‌شود — و اگر اعلان نماز را روشن کنید، کد اعلان دستگاه و اوقات نماز آینده‌ی شما.',
    ],
    fullPolicy: 'مطالعه‌ی سیاست حریم خصوصی کامل',
    agree: 'موافقم و ادامه می‌دهم',
    footer: 'با فشار "موافقم" تأیید می‌کنید که سیاست حریم خصوصی ما را خوانده و پذیرفته‌اید.',
  },
  tr: {
    welcome: 'Falak\'a hoş geldiniz',
    intro: 'Başlamadan önce, lütfen Falak\'ın verilerinizi nasıl işlediğini inceleyin.',
    points: [
      'Konumunuz namaz vakitleri ve astronomik verileri hesaplamak için kullanılır — yalnızca hesaplama için Falak sunucusuna gönderilir ve saklanmaz.',
      'Hiçbir kişisel veri toplanmaz, reklam yoktur, izleme yoktur, üçüncü taraf analizler yoktur.',
      'Yalnızca göndermeyi seçtiğiniz geri bildirim (ve isteğe bağlı e-postanız) veritabanımızda saklanır — ayrıca namaz uyarılarını açarsanız cihazınızın bildirim anahtarı ve yaklaşan namaz vakitleri.',
    ],
    fullPolicy: 'Gizlilik Politikasının tamamını okuyun',
    agree: 'Kabul Ediyorum ve Devam Et',
    footer: '"Kabul Ediyorum"a basarak Gizlilik Politikamızı okuduğunuzu ve kabul ettiğinizi onaylarsınız.',
  },
  fr: {
    welcome: 'Bienvenue dans Falak',
    intro: 'Avant de commencer, veuillez consulter comment Falak gère vos données.',
    points: [
      'Votre position est utilisée pour calculer les horaires de prière et les données astronomiques — elle est envoyée au serveur de Falak uniquement pour le calcul et n\'est jamais stockée.',
      'Aucune donnée personnelle n\'est collectée, aucune publicité, aucun suivi, aucune analyse tierce.',
      'Seuls les commentaires que vous choisissez d\'envoyer (et votre e-mail facultatif) sont enregistrés dans notre base de données — ainsi que, si vous activez les alertes de prière, le jeton de notification de votre appareil et vos prochains horaires de prière.',
    ],
    fullPolicy: 'Lire la politique de confidentialité complète',
    agree: 'J\'accepte et je continue',
    footer: 'En appuyant sur "J\'accepte", vous confirmez avoir lu et accepté notre Politique de Confidentialité.',
  },
  bn: {
    welcome: 'ফালাকে স্বাগতম',
    intro: 'শুরু করার আগে, অনুগ্রহ করে দেখুন ফালাক আপনার ডেটা কীভাবে পরিচালনা করে।',
    points: [
      'আপনার অবস্থান নামাজের সময় এবং জ্যোতির্বিদ্যা তথ্য গণনার জন্য ব্যবহৃত হয় — শুধু গণনার জন্য ফলক সার্ভারে পাঠানো হয় এবং সংরক্ষণ করা হয় না।',
      'কোনো ব্যক্তিগত ডেটা সংগ্রহ করা হয় না, কোনো বিজ্ঞাপন নেই, কোনো ট্র্যাকিং নেই, কোনো তৃতীয় পক্ষের বিশ্লেষণ নেই।',
      'শুধু আপনি যে মতামত পাঠাতে চান (এবং আপনার ঐচ্ছিক ইমেইল) তা আমাদের ডেটাবেসে সংরক্ষিত হয় — এবং নামাজের সতর্কতা চালু করলে আপনার ডিভাইসের বিজ্ঞপ্তি টোকেন ও আসন্ন নামাজের সময়।',
    ],
    fullPolicy: 'সম্পূর্ণ গোপনীয়তা নীতি পড়ুন',
    agree: 'আমি সম্মত এবং চালিয়ে যান',
    footer: '"আমি সম্মত" বাটনে চাপ দিয়ে আপনি নিশ্চিত করছেন যে আপনি আমাদের গোপনীয়তা নীতি পড়েছেন এবং গ্রহণ করেছেন।',
  },
  zh: {
    welcome: '欢迎使用 Falak',
    intro: '在开始之前，请了解 Falak 如何处理您的数据。',
    points: [
      '您的位置用于计算礼拜时间和天文数据 — 仅为计算而发送到 Falak 服务器，绝不会被存储。',
      '不收集任何个人数据，无广告，无跟踪，无第三方分析工具。',
      '只有您主动发送的反馈（以及您可选填写的电子邮件）会保存到我们的数据库；如果您开启礼拜提醒，还会保存您设备的通知令牌和即将到来的礼拜时间。',
    ],
    fullPolicy: '阅读完整的隐私政策',
    agree: '我同意并继续',
    footer: '点击"我同意"即表示您已阅读并接受我们的隐私政策。',
  },
};

const PrivacyConsentModal = () => {
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const isRTL = RTL_LANGS.has(lang);
  const t = L[lang] || L.en;

  const [visible, setVisible] = useState(false);

  // On mount, check if user has already consented
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Slight delay so the modal doesn't pop instantly on a flashy load
        const timer = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(timer);
      }
    } catch {
      // If localStorage is blocked, still show the modal each launch
      setVisible(true);
    }
  }, []);

  const handleAgree = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        agreed: true,
        timestamp: new Date().toISOString(),
        version: 'v1',
      }));
    } catch {
      /* localStorage blocked — silently continue */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(12px)',
        animation: 'falak-consent-fade-in 0.3s ease-out',
      }}
      data-testid="privacy-consent-modal"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <style>{`
        @keyframes falak-consent-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes falak-consent-slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        className="w-full max-w-md rounded-2xl border overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0F1A3B 0%, #0B132B 100%)',
          borderColor: 'rgba(14, 165, 233, 0.25)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(14, 165, 233, 0.15)',
          animation: 'falak-consent-slide-up 0.4s ease-out',
        }}
      >
        {/* Header with crescent accent */}
        <div
          className="px-6 py-5 border-b"
          style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: 'rgba(14, 165, 233, 0.15)',
                border: '1px solid rgba(14, 165, 233, 0.35)',
              }}
            >
              <Shield size={22} color="#0EA5E9" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-lg font-semibold leading-tight"
                style={{ color: '#FFFFFF' }}
                data-testid="privacy-consent-title"
              >
                {t.welcome}
              </h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p
            className="text-sm leading-relaxed mb-4"
            style={{ color: '#CBD5E1' }}
          >
            {t.intro}
          </p>

          <ul className="space-y-3 mb-5">
            {t.points.map((point, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2.5 text-[13px] leading-relaxed"
                style={{ color: '#E2E8F0' }}
              >
                <Check
                  size={16}
                  className="flex-shrink-0 mt-0.5"
                  style={{ color: '#10B981' }}
                />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
            style={{ color: '#0EA5E9' }}
            data-testid="privacy-consent-full-link"
          >
            <FileText size={13} />
            {t.fullPolicy}
            <span style={{ fontSize: 10 }}>↗</span>
          </a>
        </div>

        {/* Footer with Agree button */}
        <div
          className="px-6 py-4"
          style={{
            background: 'rgba(0, 0, 0, 0.25)',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <button
            onClick={handleAgree}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 16px rgba(14, 165, 233, 0.35)',
            }}
            data-testid="privacy-consent-agree"
          >
            {t.agree}
          </button>

          <p
            className="text-[10px] text-center mt-3 leading-relaxed"
            style={{ color: 'rgba(255, 255, 255, 0.4)' }}
          >
            {t.footer}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentModal;
